import { Moment } from "moment"
import { FormListFieldData, Rule, RuleRender } from "antd/es/form"
import { ValidatorRule } from "rc-field-form/lib/interface"

import { isGeoPickerGlobal } from "components/geoPicker"
import {
  CommunityEventGoal,
  CommunityEventMilestone,
  CommunityEventSource,
  communityEventMetrics,
} from "domains/managingJson/store/types"
import {
  COMMUNITY_EVENT_MAX_GOALS,
  COMMUNITY_EVENT_MAX_INFO_LENGTH,
  COMMUNITY_EVENT_MAX_SOURCES,
  COUNTRY_CODE_PATTERN,
  HEX_COLOR_PATTERN,
} from "./constants"
import { COMMUNITY_EVENT_DEFAULT_MIN_CONTRIBUTION } from "./defaults"
import { isAllCountries, isEveryCountryPick, toCountryCode } from "./formMapping"

const isPositive = (value: number): boolean => Number.isFinite(value) && value > 0

const reject = (message: string) => Promise.reject(new Error(message))

const isBlank = (value: unknown): value is undefined | null => value === undefined || value === null

const optionalNumberValidator = (isValid: (value: number) => boolean, message: string): Rule => ({
  validator: (_, value?: number | null) => isBlank(value) || isValid(value)
    ? Promise.resolve()
    : reject(message),
})

export const positiveNumberValidator = optionalNumberValidator(isPositive, "Must be greater than 0")

export const percentValidator = optionalNumberValidator(
  value => isPositive(value) && value <= 100,
  "Must be greater than 0 and at most 100",
)

export const hexColorValidator: Rule = {
  validator: (_, value?: string) => !value?.trim() || HEX_COLOR_PATTERN.test(value.trim())
    ? Promise.resolve()
    : reject("Must be a hex color, e.g. #FF8A2A"),
}

export const countInfoCharacters = (value?: string | null): number => Array.from(value?.trim() ?? "").length

export const infoValidator: Rule = {
  validator: (_, value?: string) => countInfoCharacters(value) <= COMMUNITY_EVENT_MAX_INFO_LENGTH
    ? Promise.resolve()
    : reject(`At most ${COMMUNITY_EVENT_MAX_INFO_LENGTH} characters`),
}

export const metricsValidator: Rule = {
  validator: (_, value: string[] = []) => {
    const unknown = value.filter(metric => !(communityEventMetrics as readonly string[]).includes(metric))

    if (!value.length) return reject("Select at least one stat")
    if (unknown.length) return reject(`Unknown stats: ${unknown.join(", ")}, pick from ${communityEventMetrics.join(", ")}`)

    return new Set(value).size === value.length ? Promise.resolve() : reject("Each stat can be listed only once")
  },
}

const asksEveryCountry = (country: string): boolean => isAllCountries(country) || isGeoPickerGlobal(country)

export const countriesValidator: Rule = {
  validator: (_, value: string[] = []) => {
    if (isEveryCountryPick(value)) return Promise.resolve()
    if (value.some(asksEveryCountry)) return reject("Pick Global alone to run in every country")

    const invalid = value.filter(country => typeof country !== "string" || !COUNTRY_CODE_PATTERN.test(toCountryCode(country)))
    if (invalid.length) return reject(`Not ISO-2 country codes: ${invalid.map(String).join(", ")}`)

    const codes = value.map(toCountryCode)

    return new Set(codes).size === codes.length ? Promise.resolve() : reject("Each country can be listed only once")
  },
}

const listSizeValidator = (min: number, max: number, noun: string): ValidatorRule => ({
  validator: (_, value?: unknown[]) => {
    const size = value?.length ?? 0

    if (size < min) return reject(`Add at least ${min} ${noun}`)

    return size > max ? reject(`At most ${max} ${noun}s`) : Promise.resolve()
  },
})

export const goalsValidator = listSizeValidator(1, COMMUNITY_EVENT_MAX_GOALS, "goal")

export const sourcesValidator = listSizeValidator(1, COMMUNITY_EVENT_MAX_SOURCES, "game")

export const milestonesValidator: ValidatorRule = {
  validator: (_, value?: unknown) => {
    return Array.isArray(value)
      ? Promise.resolve()
      : reject("No milestones list stored: click Add milestone, then remove it if none are needed")
  },
}

type ListPath = (string | number)[];

export const minContributionValidator = optionalNumberValidator(
  value => Number.isInteger(value) && value >= 1,
  "Must be a whole number of at least 1",
)

export const targetCoversMinContributionValidator = (goalPath: ListPath): RuleRender => ({ getFieldValue }) => ({
  validator: (_, value?: number | null) => {
    const minContribution: number | null | undefined = getFieldValue([...goalPath, "minContribution"])
    const required = minContribution ?? COMMUNITY_EVENT_DEFAULT_MIN_CONTRIBUTION

    return isBlank(value) || value >= required
      ? Promise.resolve()
      : reject(`Must be at least the minimum contribution (${required}${isBlank(minContribution) ? ", the default" : ""})`)
  },
})

const uniqueInList = <Row>(field: keyof Row & string, message: string) => (listPath: ListPath) => {
  const validator: RuleRender = ({ getFieldValue }) => ({
    validator: (_, value?: string | number | null) => {
      const rows: Partial<Row>[] = getFieldValue(listPath) ?? []
      const listings = rows.filter(row => !isBlank(value) && value !== "" && row?.[field] === value).length

      return listings > 1 ? reject(message) : Promise.resolve()
    },
  })

  return {
    dependencies: (fields: FormListFieldData[]) => [listPath, ...fields.map(({ name }) => [...listPath, name, field])],
    validator,
  }
}

export const uniqueGoalId = uniqueInList<CommunityEventGoal>("goalId", "This goal id is already used")

export const uniqueSourceGame = uniqueInList<CommunityEventSource>("gameId", "This game is already listed in this goal")

export const uniqueMilestonePercent = uniqueInList<CommunityEventMilestone>("pct", "This percent is already used in this goal")

export const endAtValidator: RuleRender = ({ getFieldValue }) => ({
  validator: (_, value?: Moment | null) => {
    const startAt: Moment | undefined = getFieldValue("startAt")

    return !value || !startAt || value.isAfter(startAt) ? Promise.resolve() : reject("End must be after start")
  },
})

export const visibleUntilValidator: RuleRender = ({ getFieldValue }) => ({
  validator: (_, value?: Moment | null) => {
    const endAt: Moment | undefined = getFieldValue("endAt")

    return !value || !endAt || !value.isBefore(endAt) ? Promise.resolve() : reject("Visible until must not be before end")
  },
})
